import { Link, Navigate } from 'react-router'

import { areaRoute, routes, surveyRoute, taskRoute } from '@/app/routes'
import { isApiError } from '@/shared/api'
import {
  useCountries,
  useDashboard,
  useDocuments,
  useProfile,
  useRoadmap,
  useSurveys,
  useTasks,
} from '@/shared/api/queries'
import type {
  CategoryStatus,
  DashboardTask,
  Document,
  SurveySummary,
  Task,
} from '@/shared/api/queries'
import { useDeadlineText } from '@/shared/format'
import { useLocalization, useTranslate } from '@/shared/i18n'
import type { TranslationKey } from '@/shared/i18n'
import {
  areaIcon,
  areaLabel,
  areaOf,
  evaluationsOf,
  needsAttention,
  timelineOf,
  urgencyOf,
  urgencyOfDays,
} from '@/shared/model'
import type { Urgency } from '@/shared/model'
import { useFirstName } from '@/shared/telegram'
import {
  Card,
  Empty,
  ErrorState,
  Icon,
  Progress,
  Row,
  Stack,
  StateBlock,
  Text,
  TextLink,
} from '@/shared/ui'

import { FirstRun } from './FirstRun'

const MAX_ATTENTION = 3
const CANVAS_SPANS = [
  'bento__full',
  'bento__lead',
  '',
  '',
  'bento__third',
  'bento__third',
  'bento__third',
]
const LIST_SPANS = ['bento__lead', '', '']
const MORNING_UNTIL = 12
const EVENING_FROM = 18

function greetingKey(hour: number): TranslationKey {
  if (hour < MORNING_UNTIL) {
    return 'home.greeting.morning'
  }

  return hour < EVENING_FROM ? 'home.greeting.afternoon' : 'home.greeting.evening'
}

function flagOf(code: string | undefined): string {
  if (!code || code.length !== 2) {
    return '🌍'
  }

  return String.fromCodePoint(...[...code.toUpperCase()].map((c) => 0x1f1a5 + c.charCodeAt(0)))
}

function urgencyOfDashboardTask(task: DashboardTask): Urgency {
  return urgencyOfDays(task.days_left, task.hard_deadline)
}

function Hero({ country, code, attention }: { country: string; code: string; attention: number }) {
  const t = useTranslate()
  const firstName = useFirstName()

  const hour = new Date().getHours()
  const key = greetingKey(hour)

  return (
    <Stack as="header" gap="xs">
      <Text as="h1" role="heading-2">
        {firstName
          ? t(key, { name: firstName })
          : t(`${key.replace('greeting.', 'greeting.plain.')}` as TranslationKey, { name: '' })}
      </Text>
      <Stack direction="row" gap="sm" wrap>
        <Text as="span" role="label" tone="muted">
          {flagOf(code)} {country}
        </Text>
        <Text as="span" role="label" tone="muted">
          ·
        </Text>
        <Text
          as="span"
          role="label"
          tone="tone"
          className={attention > 0 ? 'tone--attention' : 'tone--done'}
        >
          {attention > 0
            ? t('home.attention.count', { count: attention })
            : t('home.attention.none')}
        </Text>
      </Stack>
    </Stack>
  )
}

function UpNext({ tasks, documents }: { tasks: Task[]; documents: Document[] }) {
  const t = useTranslate()
  const { language } = useLocalization()

  const next = timelineOf(tasks, documents)[0]

  const date = next
    ? new Intl.DateTimeFormat(language, { month: 'short', day: 'numeric' }).format(
        new Date(next.date),
      )
    : undefined

  const urgency: Urgency = next
    ? urgencyOf({ deadline: next.date, hard_deadline: next.task?.hard_deadline ?? true })
    : 'neutral'

  const solid = urgency === 'critical' || urgency === 'attention' || urgency === 'done'

  return (
    <Card
      inverse={!solid}
      solid={solid}
      className={`tone--${urgency}`}
      to={next?.task ? taskRoute(next.task.id) : routes.timeline}
    >
      <Text role="eyebrow" tone="on-inverse">
        {t('home.next')}
      </Text>
      <Stack gap="xs">
        <Text role="date" tone="on-inverse">
          {date ?? '—'}
        </Text>
        <Text role="label" tone="on-inverse">
          {next?.task?.title ??
            (next?.document
              ? t('timeline.document.expires', { document: t('documents.title') })
              : t('home.next.none'))}
        </Text>
      </Stack>
    </Card>
  )
}

function VaultTile({ documents }: { documents: Document[] }) {
  const t = useTranslate()

  const expiring = documents.filter((document) =>
    needsAttention(urgencyOf({ deadline: document.expires_on, hard_deadline: true })),
  ).length

  return (
    <Card to={routes.documents}>
      <Text role="eyebrow" tone="muted">
        {t('home.vault')}
      </Text>
      <Stack gap="xs">
        <Text role="heading-2">{documents.length}</Text>
        <Text role="caption" tone="muted">
          {t('home.vault.count')}
          {expiring > 0 ? (
            <>
              {' · '}
              <Text as="span" role="caption" tone="tone" className="tone--attention">
                {t('home.vault.expiring', { count: expiring })}
              </Text>
            </>
          ) : null}
        </Text>
      </Stack>
    </Card>
  )
}

function TasksTile({ tasks }: { tasks: Task[] }) {
  const t = useTranslate()

  const open = tasks.filter((task) => task.status === 'open').length
  const done = tasks.length - open

  return (
    <Card to={routes.tasks}>
      <Text role="eyebrow" tone="muted">
        {t('home.tasks')}
      </Text>
      <Stack gap="xs">
        <Stack direction="row" gap="xs">
          <Text as="span" role="heading-2">
            {open}
          </Text>
          <Text as="span" role="caption" tone="muted">
            {t('home.tasks.open')}
          </Text>
        </Stack>
        {tasks.length > 0 ? (
          <Progress
            value={done}
            max={tasks.length}
            label={t('home.tasks')}
            caption={t('home.tasks.progress', { done, total: tasks.length })}
          />
        ) : null}
      </Stack>
    </Card>
  )
}

function AreaTile({ section }: { section: CategoryStatus }) {
  const t = useTranslate()

  const open = section.total - section.done
  const urgency: Urgency =
    open === 0 ? 'done' : urgencyOf({ deadline: section.next_deadline, hard_deadline: false })

  return (
    <li className="bento__third">
      <Link className={`tile tone--${urgency}`} to={areaRoute(section.category)} viewTransition>
        <Icon size="md">{areaIcon(section.category)}</Icon>
        <Text as="span" role="label">
          {t(areaLabel(section.category))}
        </Text>
        <Text as="span" role="caption" tone="tone">
          {open === 0 ? t('area.clear') : t('area.todo', { count: open })}
        </Text>
      </Link>
    </li>
  )
}

// Invitation is the way into a questionnaire, and it is deliberately not a
// task: a task has an official source behind it (PRD §13), and nobody owes the
// state an answer to a question the product asked.
function Invitation({ survey }: { survey: SurveySummary }) {
  const t = useTranslate()

  return (
    <Card to={surveyRoute(survey.tag)}>
      <Text role="eyebrow" tone="muted">
        {t('home.survey.title')}
      </Text>
      <Stack gap="xs">
        <Text role="label">{t('home.survey.body', { survey: survey.title })}</Text>
        <Text role="caption" tone="accent">
          {t('home.survey.cta')}
        </Text>
      </Stack>
    </Card>
  )
}

function SkeletonTiles({ spans }: { spans: string[] }) {
  return spans.map((span, index) => (
    <li key={index} aria-busy="true" {...(span ? { className: span } : {})}>
      <div className="skeleton__line" />
    </li>
  ))
}

function ListTiles({
  tasks,
  documents,
  pending,
  error,
  onRetry,
}: {
  tasks: Task[]
  documents: Document[]
  pending: boolean
  error: unknown
  onRetry: () => void
}) {
  if (pending) {
    return <SkeletonTiles spans={LIST_SPANS} />
  }

  if (error !== undefined && error !== null) {
    return (
      <li className="bento__full">
        <ErrorState error={error} onRetry={onRetry} />
      </li>
    )
  }

  return (
    <>
      <li className="bento__lead">
        <UpNext tasks={tasks} documents={documents} />
      </li>
      <li>
        <VaultTile documents={documents} />
      </li>
      <li>
        <TasksTile tasks={tasks} />
      </li>
    </>
  )
}

export function DashboardPage() {
  const t = useTranslate()
  const deadline = useDeadlineText()
  const firstName = useFirstName()

  const dashboard = useDashboard()
  const roadmap = useRoadmap()
  const tasks = useTasks()
  const documents = useDocuments()
  const profile = useProfile()
  const countries = useCountries()
  const surveys = useSurveys()

  // A 404 here means the profile was never filled in, and onboarding opens with
  // an invitation of its own. Standing another one in front of it made the
  // person press a button to be shown a button.
  if (dashboard.error && isApiError(dashboard.error) && dashboard.error.status === 404) {
    return <Navigate to={routes.onboarding} replace />
  }

  const country = countries.data?.find(
    (candidate) => candidate.code === profile.data?.residence_country,
  )
  const where = country?.name ?? profile.data?.residence_country ?? ''

  // Nothing has been read yet, so every date on this screen would be a guess.
  // The roadmap exists — it came out of the answers — but it has no document to
  // pin a deadline to, and a home full of empty tiles says less than one card
  // that names the next step.
  if (documents.data?.length === 0) {
    return (
      <FirstRun
        name={firstName}
        country={where}
        flag={flagOf(profile.data?.residence_country ?? '')}
      />
    )
  }

  const evaluations = evaluationsOf(roadmap.data)
  const unfinished = surveys.data?.find((survey) => survey.status !== 'completed')

  const attention = (
    dashboard.data ? [...dashboard.data.today, ...dashboard.data.upcoming] : []
  ).filter((task) => needsAttention(urgencyOfDashboardTask(task)))

  return (
    <Stack gap="md">
      <Hero
        country={where}
        code={profile.data?.residence_country ?? ''}
        attention={attention.length}
      />

      <StateBlock
        pending={dashboard.isPending}
        error={dashboard.error}
        onRetry={() => void dashboard.refetch()}
        skeleton={
          <ul className="bento">
            <SkeletonTiles spans={CANVAS_SPANS} />
          </ul>
        }
      >
        <ul className="bento">
          <li className="bento__full">
            {attention.length === 0 ? (
              <Empty title={t('home.clear.title')} body={t('home.clear.body')}>
                <TextLink to={routes.documents}>{t('documents.add')}</TextLink>
              </Empty>
            ) : (
              <Card>
                <Stack direction="row" justify="between" gap="sm">
                  <Stack direction="row" gap="sm" className="tone--attention">
                    <span className="dot" aria-hidden="true" />
                    <Text as="h2" role="eyebrow" tone="muted">
                      {t('home.attention')}
                    </Text>
                  </Stack>
                  <TextLink to={routes.tasks}>
                    {t('home.all', { count: attention.length })}
                  </TextLink>
                </Stack>
                <Stack as="ul" gap="xs">
                  {attention.slice(0, MAX_ATTENTION).map((task) => (
                    <Row
                      key={task.id}
                      variant="plain"
                      to={taskRoute(task.id)}
                      className={`tone--${urgencyOfDashboardTask(task)}`}
                      icon={areaIcon(areaOf(task, evaluations) ?? 'other')}
                      title={task.title}
                      meta={deadline(task.deadline)}
                    />
                  ))}
                </Stack>
              </Card>
            )}
          </li>

          {unfinished ? (
            <li className="bento__full">
              <Invitation survey={unfinished} />
            </li>
          ) : null}

          <ListTiles
            tasks={tasks.data ?? []}
            documents={documents.data ?? []}
            pending={tasks.isPending || documents.isPending}
            error={tasks.error ?? documents.error}
            onRetry={() => {
              void tasks.refetch()
              void documents.refetch()
            }}
          />

          {dashboard.data?.status.map((section) => (
            <AreaTile key={section.category} section={section} />
          ))}
        </ul>
      </StateBlock>
    </Stack>
  )
}
